"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Loader, ShieldCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FormularioDeContactoB2b() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  return <section id="formulario-de-contacto-b2b" className="dark bg-background text-foreground section container px-4 py-48">
      <div className="container">
        <div ref={ref} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="text-center mb-12">
            <h1 className="font-serif text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Solicite Información para Tiendas y Distribuidores
            </h1>
            <p className="font-sans text-muted-foreground text-lg max-w-2xl mx-auto">
              Complete el formulario y nos pondremos en contacto con usted para ofrecerle nuestro aceite de oliva virgen extra de alta calidad.
            </p>
          </div>

          <FormProvider className="w-full" formId="contact_form_for_stores" sectionName="formulario-de-contacto-b2b" defaultValues={{
          name: "",
          company_name: "",
          email: "",
          phone: "",
          business_type: "",
          message: ""
        }} validationRules={{
          name: {
            required: true
          },
          company_name: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            pattern: "phone"
          },
          business_type: {
            required: true
          },
          message: {
            required: true
          }
        }} submitText="Enviar Solicitud" successMessage="¡Gracias por su interés! Nos pondremos en contacto con usted pronto." errorMessage="No se pudo enviar el formulario. Por favor, inténtelo de nuevo.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="border-none bg-transparent">
        <CardContent className="p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="size-8 text-primary" />
            </div>
          </div>
          <h3 className="font-serif text-2xl font-bold mb-3 text-foreground">
            {successMessage}
          </h3>
          <p className="font-sans text-muted-foreground mb-6">
            Un miembro de nuestro equipo revisará su solicitud y se pondrá en contacto con usted a la brevedad.
          </p>
          <Button onClick={resetForm} className="btn">
            Enviar Otra Solicitud
          </Button>
        </CardContent>
      </Card>;
  }
  return <Card className="border-none bg-card">
      <div className="h-2 bg-primary rounded-t-xl" />
      <CardHeader className="pb-4">
        <CardTitle className="font-serif text-2xl text-card-foreground">
          Formulario de Contacto B2B
        </CardTitle>
      </CardHeader>
      <Separator className="mb-6" />
      <CardContent>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "El nombre es obligatorio",
            minLength: {
              value: 2,
              message: "Mínimo 2 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Nombre y Apellidos</FormLabel>
                  <FormControl>
                    <Input placeholder="Juan Pérez" className="input" {...field} />
                  </FormControl>
                  <FormDescription>Introduce tu nombre completo.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="company_name" rules={{
            required: "El nombre de la empresa es obligatorio",
            minLength: {
              value: 2,
              message: "Mínimo 2 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Nombre de la Empresa</FormLabel>
                  <FormControl>
                    <Input placeholder="Aceite Paco y Herminia" className="input" {...field} />
                  </FormControl>
                  <FormDescription>
                    Nombre de tu tienda, distribuidora, restaurante, etc.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            required: "El correo electrónico es obligatorio",
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Correo electrónico inválido"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Correo Electrónico</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="juan@empresa.com" className="input" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            pattern: {
              value: /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/,
              message: "Teléfono inválido"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Teléfono</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+31 6 1234 5678" className="input" {...field} />
                  </FormControl>
                  <FormDescription>
                    Opcional, pero nos ayuda a responder más rápido.
                  </FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="business_type" rules={{
            required: "Selecciona el tipo de negocio"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Tipo de Negocio</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Selecciona una opción" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Tienda">Tienda</SelectItem>
                      <SelectItem value="Distribuidor">Distribuidor</SelectItem>
                      <SelectItem value="Restaurante">Restaurante</SelectItem>
                      <SelectItem value="Hotel">Hotel</SelectItem>
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "El mensaje es obligatorio",
            minLength: {
              value: 10,
              message: "Mínimo 10 caracteres"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel>Mensaje</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Cuéntanos tus necesidades o preguntas..." className="min-h-32 resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="bg-accent/50 rounded-lg p-4">
              <div className="flex gap-3">
                <ShieldCheck className="size-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-sm text-accent-foreground mb-1">
                    Nota Importante
                  </h4>
                  <p className="font-sans text-sm text-accent-foreground/80">
                    No vendemos online para proteger a nuestros socios gourmet. Trabajamos exclusivamente con tiendas delicatessen, distribuidores y restaurantes.
                  </p>
                </div>
              </div>
            </div>

            {submitError && <div className="p-4 bg-accent/20 border border-accent/50 rounded-lg">
                <p className="text-foreground text-sm font-medium">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full btn">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-5" />
                  Enviando...
                </> : "Enviar Solicitud"}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}